import sliderCss from 'blaze-slider/dist/blaze.css?url'

import {createFileRoute, notFound} from '@tanstack/react-router'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useIsClient, useLocalStorage} from 'usehooks-ts'
import {useSuspenseQuery} from '@tanstack/react-query'
import {useTranslations} from 'use-intl'
import {uniqBy} from 'lodash-es'
import {perfectFitCollectionDetailsQueryOptions} from '~/client/fn/perfectFit'
import PerfectFitProducts from '~/components/perfect-fit/PerfectFitProducts'
import {DesignCollectionItem} from '~/client/spreadshop/missing-types'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import useLocaleParams from '~/hooks/useLocaleParams'
import Stack from '~/components/perfect-fit/Stack'
import {Button} from '~/components/common/Button'
import Card from '~/components/common/Card'
import seo from '~/utils/seo'

export const Route = createFileRoute('/$lang/_layout/heart-or-pass')({
    component: RouteComponent,
    loader: async ({context}) => {
        const collection = await context.queryClient.ensureQueryData(
            perfectFitCollectionDetailsQueryOptions(context.locale)
        )

        if (!collection) {
            throw notFound()
        }

        return collection
    },
    head: ({loaderData, params}) => {
        const collection = loaderData
        if (!collection) {
            return {}
        }

        const lang = params.lang
        const canonicalUrl = `https://gravity-shirts.com/${lang}/heart-or-pass`
        const fallbackImageSrc = collection.designs[0]?.image.src ?? ''
        const metaTags = seo({
            title: collection.seo?.title ?? collection.name,
            description: collection.seo?.description ?? collection.description ?? undefined,
            ogUrl: canonicalUrl,
            locale: lang,
            image: {
                src:
                    collection.seo?.image ??
                    `${fallbackImageSrc}&width=1200&height=630&backgroundColor=1c1d2c`,
                width: 1200,
                height: 630,
            },
        })

        return {
            meta: metaTags.length > 0 ? metaTags : undefined,
            links: [
                {
                    rel: 'preload',
                    href: sliderCss,
                    as: 'style',
                },
                {
                    rel: 'stylesheet',
                    href: sliderCss,
                },
            ],
        }
    },
})

const DESIGN_PAGE_COUNT = 5

function RouteComponent() {
    const params = useLocaleParams()
    const t = useTranslations('perfectFit')
    const isClient = useIsClient()
    const {data: collection} = useSuspenseQuery(
        perfectFitCollectionDetailsQueryOptions(params.locale)
    )
    const [page, setPage] = useState<number>(1)
    const [likedDesigns, setLikedDesigns] = useLocalStorage<DesignCollectionItem[]>(
        'likedDesigns',
        [],
        {
            serializer: JSON.stringify,
            deserializer: (str) => (!!str ? JSON.parse(str) : []),
        }
    )
    const [showResults, setShowResults] = useState<boolean>(false)

    useEffect(() => {
        if (!isClient) {
            return
        }

        setShowResults(likedDesigns.length > 0)
    }, [isClient])

    const reset = useCallback(() => {
        setPage(1)
        setLikedDesigns([])
        setShowResults(false)
    }, [setLikedDesigns])

    const [designs, hasMoreDesigns] = useMemo(() => {
        const allDesigns = [...(collection?.designs ?? [])]
        const paginatedDesigns = allDesigns
            .reverse()
            .splice((page - 1) * DESIGN_PAGE_COUNT, DESIGN_PAGE_COUNT)
        const hasMoreDesigns = allDesigns.length > (page - 1) * DESIGN_PAGE_COUNT
        return [paginatedDesigns, hasMoreDesigns]
    }, [collection?.designs, page])

    const handleFinish = useCallback(
        (newLikedDesigns: DesignCollectionItem[], _: DesignCollectionItem[]) => {
            const designs = uniqBy([...likedDesigns, ...newLikedDesigns], 'id')
            setLikedDesigns(designs)

            if (designs.length === 0 || (designs.length < 3 && hasMoreDesigns)) {
                setPage((page) => (hasMoreDesigns ? page + 1 : 1))
            } else {
                setShowResults(true)
            }
        },
        [setLikedDesigns, hasMoreDesigns, likedDesigns]
    )

    return (
        <div className="mx-auto max-w-screen-xl py-6 lg:py-12">
            <Breadcrumbs
                className="mb-2 md:mb-6 px-4 sm:px-6"
                items={[
                    {name: t('breadcrumbs.home'), to: '/'},
                    {name: collection?.name ?? '', to: '.'},
                ]}
            />
            <h1 className="text-2xl font-medium mb-6 px-4 sm:px-6">{collection?.name}</h1>
            {!!designs && !showResults && (
                <div className="w-full px-4 sm:px-6 overflow-x-hidden md:overflow-visible">
                    <div className="mx-auto size-full aspect-square max-w-[100svw] sm:max-w-md my-16">
                        <h2 className="sr-only">{t('stack.title')}</h2>
                        <Stack designs={designs} onFinish={handleFinish} />
                    </div>
                    <Card className="mx-auto max-w-screen-sm py-4 px-4 sm:px-6">
                        <h2 className="text-lg font-bold mb-2">{t('howTo.title')}</h2>
                        <div className="space-y-2">
                            {t.rich('howTo.description', {
                                l: (chunks) => (
                                    <span className="text-red-500 font-medium">{chunks}</span>
                                ),
                                r: (chunks) => (
                                    <span className="text-green-500 font-medium">{chunks}</span>
                                ),
                                p: (chunks) => <p>{chunks}</p>,
                            })}
                        </div>
                    </Card>
                </div>
            )}

            {showResults && isClient && (
                <div className="px-4 sm:px-6">
                    <Card className="py-4 px-4 sm:px-6 mb-6">
                        <h2 className="text-lg font-bold mb-2">{t('results.title')}</h2>
                        <p>{t('results.description')}</p>
                    </Card>
                    <div className="space-y-8">
                        {likedDesigns.map((article, index) => (
                            <PerfectFitProducts
                                design={article}
                                preload={index <= 2}
                                key={article.id}
                            />
                        ))}
                    </div>
                    <div className="sticky bottom-4 w-full mt-10">
                        <Button onClick={reset} className="max-w-screen-sm mx-auto w-full">
                            {t('reset')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
